import { api } from '../lib/api'
import { clearFlow, startFlow } from '../telemetry/flow'
import { getClientRequestId } from '../telemetry/request'

export type WalletSummary = {
  summary: {
    fiat_estimate: string
    pixe_balance: string
    change_24h_pct: string
    address: string
    backup_confirmed_at?: string | null
    last_recovery_kit_generated_at?: string | null
  }
  assets: Array<{ symbol: string; name: string; balance: string; fiat_value: string }>
  onchain?: {
    address_ss58: string
    pixe_free: string
    pixe_reserved: string
    pixe_locked: string
    updated_at?: string
  }
  ledger?: {
    available: string
    pending: string
    held: string
  }
  effective_for_posting?: {
    pixe_required: string
    can_post: boolean
    reason?: string
  }
}

export async function getWalletSummary(opts?: { reconcile?: boolean }) {
  const params: Record<string, any> = {}
  if (opts?.reconcile) params.reconcile = 1
  const { data } = await api.get<WalletSummary>('/api/wallet/', { params })
  return data
}

export async function linkWallet(ss58: string) {
  const { data } = await api.post<{ ok: boolean; ss58_address: string }>('/api/wallet/link', { ss58 })
  return data
}

export type WalletNFTItem = {
  id: number
  handle?: string
  title: string
  subtitle?: string
  image_url?: string
  collection?: string
  collection_id?: string
  token_id?: string
  tx_hash?: string
  block_number?: number | null
  block_hash?: string
  minted_at?: string | null
  on_chain?: boolean
  owner_wallet?: string
}

export type WalletNFTPage = { items: WalletNFTItem[]; next: string | null }

export async function getWalletNFTs(cursor?: string) {
  const params: Record<string, any> = {}
  if (cursor) params.cursor = cursor
  try {
    const { data } = await api.get<WalletNFTPage>('/api/wallet/nfts', { params })
    return data
  } catch (error: any) {
    const status = error?.response?.status
    if (status === 400 || status === 404) {
      return { items: [], next: null }
    }
    throw error
  }
}

// ---- Activity ----
export type ActivityItem = {
  id: string
  journal_entry_id?: number
  ts: string
  kind: string
  title: string
  description?: string | null
  amount: number
  fiat?: number | null
  onchain_status?: string
  onchain_tx_hash?: string
  onchain_block?: number | null
}
export type ActivityPage = { items: ActivityItem[]; next: string | null }
export async function getWalletActivity(cursor?: string) {
  const params: Record<string, any> = {}
  if (cursor) params.cursor = cursor
  const { data } = await api.get<ActivityPage>('/api/wallet/activity', { params })
  return data
}

export type WalletTicketSummary = {
  post_id: number
  event_title: string
  starts_at?: string | null
  ends_at?: string | null
  city_label: string
  venue_name: string
  address: string
  qty: number
  status: 'active' | 'redeemed' | 'cancelled' | 'past'
  redeemed_qty?: number
  last_order_id?: number | null
  purchased_at?: string | null
  cover_image_url?: string
  receipt_hash?: string
  receipt_status?: 'pending' | 'anchored' | 'failed' | string
  receipt_tx_hash?: string
  receipt_block_number?: number | null
  ticket_serials?: string[]
  ticket_qr_payloads?: Array<Record<string, any>>
}

export type WalletTicketsPage = { results: WalletTicketSummary[] }
export async function getWalletTickets() {
  const { data } = await api.get<WalletTicketsPage>('/api/wallet/tickets')
  return data
}

// ---- Price ----
export async function getPixePrice() {
  const { data } = await api.get<{ pixe_usd: number }>('/api/wallet/price')
  return data.pixe_usd
}

// ---- Buy / Sell / Swap ----
export async function quotePixePack(pixeAmount: 10 | 25 | 100) {
  const { data } = await api.get<{ pixe_amount: number; base_cents: number; fee_cents: number; total_cents: number }>(
    '/api/wallet/buy/quote',
    { params: { pixe_amount: pixeAmount } },
  )
  return data
}

export async function createPixePurchaseIntent(payload: { pack: 10 | 25 | 100; email?: string }) {
  const { data } = await api.post<{
    client_secret: string
    payment_intent_id: string
    amount_cents: number
    fee_cents: number
    total_cents: number
    currency: string
  }>('/api/wallet/pixe/purchase/create-intent/', payload)
  return data
}

export async function getPixePurchaseStatus(paymentIntentId: string) {
  const { data } = await api.get<{
    payment_intent_id: string
    status: 'created' | 'requires_action' | 'succeeded' | 'failed'
    credited: boolean
    credited_at: string | null
    pack_pixe: number
    amount_cents: number
    fee_cents: number
    total_cents: number
  }>('/api/wallet/pixe/purchase/status/', { params: { payment_intent_id: paymentIntentId } })
  return data
}

export async function reconcilePixePurchase(paymentIntentId: string) {
  const { data } = await api.post<{
    status: 'credited' | 'pending' | 'not_found' | 'failed'
    credited_pixe?: number
    message?: string
  }>('/api/wallet/pixe/purchase/reconcile/', { payment_intent_id: paymentIntentId })
  return data
}

export async function buyPixe(payload: { amount_usd: number; expected_pixe?: number; attempt_id?: string; payment_method?: 'stripe_existing' | 'stripe_new' | 'pixeloop_web' | 'existing' | 'new'; inline?: boolean; return_path?: string }) {
  const { data } = await api.post<{ session_id: string; status: string; client_secret?: string; checkout_url?: string; pixe_quote: string }>('/api/wallet/buy', payload)
  return data
}

export async function getBuyStatus(sessionId: string) {
  const { data } = await api.get<{ session_id: string; state: string; pixe_amount?: string; usd?: string }>('/api/wallet/buy/status', { params: { session_id: sessionId } })
  return data
}

export async function reconcilePurchase(checkoutSessionId?: string) {
  const params: Record<string, any> = {}
  if (checkoutSessionId) params.checkout_session_id = checkoutSessionId
  const { data } = await api.get<{
    status: 'ok' | 'pending' | 'not_found'
    wallet: { balance_pixe: string; balance_usd: string }
    ledger: any[]
  }>('/api/wallet/reconcile', { params })
  return data
}

export async function sellPixe(payload: { amount_pixe: number; expected_usd?: number; payout_method?: 'bank_existing' | 'bank_new' }) {
  const { data } = await api.post<{ session_id: string; status: string }>('/api/wallet/sell', payload)
  return data
}

export async function swap(payload: { from_unit: 'USD' | 'PIXE'; from_amount: number; expected_out?: number }) {
  const { data } = await api.post<{ txid: string; status: 'succeeded' }>('/api/wallet/swap', payload)
  return data
}

// ---- Send PIXE ----
export type SendInput = { to: string; amount: number; memo?: string }
export type SendResult = {
  txid?: string
  tx_hash?: string
  status?: 'submitting' | 'pending' | 'submitted' | 'in_block' | 'finalized' | 'confirmed' | 'failed'
  journal_entry_id?: number
  amount_pixe?: string
  to_ss58?: string
  from_ss58?: string
  message?: string
}
export async function sendPixe(
  payload: SendInput,
  opts?: { idempotencyKey?: string; clientRequestId?: string; clientAction?: string },
) {
  const flow = startFlow(opts?.clientAction || 'wallet_send_pixe')
  const headers: Record<string, string> = {}
  if (opts?.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey
  const requestId = opts?.clientRequestId || getClientRequestId()
  headers['X-Client-Request-Id'] = requestId
  headers['X-Client-Action'] = opts?.clientAction || 'wallet_send_pixe'
  headers['X-Client-Flow-Id'] = flow.flowId
  try {
    const { data } = await api.post<SendResult>('/api/wallet/send', payload, Object.keys(headers).length ? { headers } : undefined)
    return data
  } finally {
    clearFlow(flow.flowId)
  }
}

export type WalletReceipt = {
  journal_entry_id: number
  amount_pixe: string
  amount_display: string
  memo?: string
  created_at: string
  sender: { user_id?: number; display_name?: string; handle?: string; ss58?: string }
  recipient: { user_id?: number; display_name?: string; handle?: string; ss58?: string }
  blockchain: {
    tx_hash?: string
    chain_status?: string
    block_number?: number | null
    fee_pixe?: string | null
    network?: string
    explorer_url?: string
  }
  receipt_url: string
}

export async function getWalletReceipt(entryId: string | number) {
  const { data } = await api.get<WalletReceipt>(`/api/wallet/receipts/${entryId}`)
  return data
}

export type RecoveryKitStatus = {
  wallet_ss58?: string | null
  wallet_network?: string | null
  backup_confirmed_at?: string | null
  last_recovery_kit_generated_at?: string | null
}

export async function getRecoveryKitStatus() {
  const { data } = await api.get<RecoveryKitStatus>('/api/wallet/export/status')
  return data
}

export async function startRecoveryKitExport(payload: { password: string; otp_code?: string }) {
  const { data } = await api.post<{ export_token_id: string; expires_in_seconds: number }>('/api/wallet/export/start', payload)
  return data
}

export async function fetchRecoveryMnemonic(payload: { export_token_id: string }) {
  const { data } = await api.post<{ mnemonic: string }>('/api/wallet/export/mnemonic', payload)
  return data
}

export async function confirmRecoveryKit(payload: { confirmed: boolean; method: 'recovery_kit' }) {
  const { data } = await api.post<{ ok: boolean }>('/api/wallet/backup-confirmed', payload)
  return data
}
